/**
 * Device identity and prekey publication (the "server side" of
 * X3DH that a device uploads to be able to receive asynchronous sessions).
 *
 * A Susurro identity belongs to a **device** (guest-first: it does not
 * require an account). If the user links several devices to an account,
 * each one has its own identity and its own set of prekeys; messages
 * are encrypted per-device (fan-out). The server only custodies
 * PUBLIC material (bundles) and opaque envelopes.
 *
 * Bundle published by Bob so Alice can write to him while he is offline:
 *   - IK   : DH identity key (long-lived)
 *   - IK_sig: signing identity key (long-lived)
 *   - SPK  : signed prekey DH (periodic rotation) + signature by IK_sig
 *   - OPK  : one-time prekeys DH (consumable, one per incoming session)
 */

import { bytesToBase64 } from "./crypto/encoding";
import {
  generateDhKeyPair,
  generateSigningKeyPair,
  type RawKeyPair,
  sign,
  verify,
} from "./crypto/dh";

const SPK_SIG_CONTEXT = "susurro:spk:v1";

export interface DeviceIdentity {
  readonly deviceId: string;
  /** DH identity key (X3DH IK). */
  readonly identityDh: RawKeyPair;
  /** Signing identity key (authenticates prekeys and sender-keys). */
  readonly identitySig: RawKeyPair;
  /** Current signed prekey. */
  signedPreKey: SignedPreKeyPrivate;
  /** Unconsumed one-time prekeys (local PRIVATE keys), by id. */
  oneTimePreKeys: Map<string, RawKeyPair>;
  /** Ids of OPKs already delivered by the directory (so as not to hand out the same one twice). */
  publishedOpkIds: Set<string>;
}

export interface SignedPreKeyPrivate {
  readonly keyId: string;
  readonly keyPair: RawKeyPair;
  /** Signature of the SPK public key by the signing identity key. */
  readonly signature: Uint8Array;
}

/** PUBLIC material that is uploaded to the server directory (nothing secret). */
export interface PreKeyBundle {
  readonly deviceId: string;
  readonly identityDhPub: Uint8Array;
  readonly identitySigPub: Uint8Array;
  readonly signedPreKeyId: string;
  readonly signedPreKeyPub: Uint8Array;
  readonly signedPreKeySignature: Uint8Array;
  /** Optional: it can run out. If missing, X3DH uses 3 DH instead of 4. */
  readonly oneTimePreKeyId?: string;
  readonly oneTimePreKeyPub?: Uint8Array;
}

let counter = 0;
function freshId(prefix: string): string {
  counter += 1;
  return `${prefix}-${bytesToBase64(crypto.getRandomValues(new Uint8Array(9)))}-${counter}`;
}

async function makeSignedPreKey(identitySig: RawKeyPair): Promise<SignedPreKeyPrivate> {
  const keyPair = await generateDhKeyPair();
  const signature = await sign(
    identitySig.privateKey,
    new TextEncoder().encode(SPK_SIG_CONTEXT + bytesToBase64(keyPair.publicKey)),
  );
  return { keyId: freshId("spk"), keyPair, signature };
}

/** Creates a new device identity with its SPK and N one-time prekeys. */
export async function createDeviceIdentity(
  deviceId: string,
  oneTimeCount = 8,
): Promise<DeviceIdentity> {
  const identityDh = await generateDhKeyPair();
  const identitySig = await generateSigningKeyPair();
  const signedPreKey = await makeSignedPreKey(identitySig);
  const oneTimePreKeys = new Map<string, RawKeyPair>();
  for (let i = 0; i < oneTimeCount; i++) {
    oneTimePreKeys.set(freshId("opk"), await generateDhKeyPair());
  }
  return {
    deviceId,
    identityDh,
    identitySig,
    signedPreKey,
    oneTimePreKeys,
    publishedOpkIds: new Set(),
  };
}

/** Rotates the signed prekey (call periodically; the old one is kept for a while). */
export async function rotateSignedPreKey(identity: DeviceIdentity): Promise<void> {
  identity.signedPreKey = await makeSignedPreKey(identity.identitySig);
}

/** Replenishes one-time prekeys (call when the directory reports few). */
export async function replenishOneTimePreKeys(
  identity: DeviceIdentity,
  count: number,
): Promise<string[]> {
  const ids: string[] = [];
  for (let i = 0; i < count; i++) {
    const id = freshId("opk");
    identity.oneTimePreKeys.set(id, await generateDhKeyPair());
    ids.push(id);
  }
  return ids;
}

/**
 * Assembles the public bundle to publish. It delivers the NEXT undistributed
 * one-time prekey (marks its id as delivered, but does NOT delete the private key: the
 * device needs it to respond; it is deleted upon CONSUMING it in X3DH). If
 * all have already been distributed, it returns the bundle without an OPK (X3DH uses 3 DH).
 */
export function publishBundle(identity: DeviceIdentity): PreKeyBundle {
  let oneTimePreKeyId: string | undefined;
  let oneTimePreKeyPub: Uint8Array | undefined;
  for (const [id, pair] of identity.oneTimePreKeys) {
    if (!identity.publishedOpkIds.has(id)) {
      oneTimePreKeyId = id;
      oneTimePreKeyPub = pair.publicKey;
      identity.publishedOpkIds.add(id);
      break;
    }
  }
  return {
    deviceId: identity.deviceId,
    identityDhPub: identity.identityDh.publicKey,
    identitySigPub: identity.identitySig.publicKey,
    signedPreKeyId: identity.signedPreKey.keyId,
    signedPreKeyPub: identity.signedPreKey.keyPair.publicKey,
    signedPreKeySignature: identity.signedPreKey.signature,
    oneTimePreKeyId,
    oneTimePreKeyPub,
  };
}

/** Verifies that the bundle's SPK is signed by the identity key. */
export async function verifyBundle(bundle: PreKeyBundle): Promise<boolean> {
  return verify(
    bundle.identitySigPub,
    new TextEncoder().encode(SPK_SIG_CONTEXT + bytesToBase64(bundle.signedPreKeyPub)),
    bundle.signedPreKeySignature,
  );
}
